import React, { useState } from 'react';
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Chip,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  AccountCircle as ProfileIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Feedback as FeedbackIcon,
  ListAlt as RequestsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * UserMenu Component
 *
 * User profile dropdown menu
 *
 * Features:
 * - User avatar with initials
 * - Role badge display
 * - Profile, Settings, Feedback, My Requests, Sign Out options
 * - Dropdown menu with icons
 * - User name and email display
 *
 * @component
 * @returns {JSX.Element} User menu dropdown
 */
export const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const { authState, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  // ===== Event Handlers =====

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleMenuClose();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // ===== Helper Functions =====

  /**
   * Get user initials from display name or email
   */
  const getUserInitials = (): string => {
    if (authState.user?.displayName) {
      const names = authState.user.displayName.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return names[0].substring(0, 2).toUpperCase();
    }
    if (authState.user?.email) {
      return authState.user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  /**
   * Get role display label and color
   */
  const getRoleInfo = (): { label: string; color: 'primary' | 'secondary' | 'success' | 'warning' | 'info' } => {
    const role = authState.user?.role || 'viewer';
    switch (role.toLowerCase()) {
      case 'admin':
        return { label: 'Admin', color: 'primary' };
      case 'editor':
        return { label: 'Editor', color: 'success' };
      case 'contributor':
        return { label: 'Contributor', color: 'info' };
      default:
        return { label: 'Viewer', color: 'secondary' };
    }
  };

  // ===== Render =====

  if (!authState.user) {
    return null;
  }

  const roleInfo = getRoleInfo();

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        size="small"
        sx={{
          ml: 1,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: 'primary.main',
            fontSize: '0.9rem',
            fontWeight: 600,
          }}
        >
          {getUserInitials()}
        </Avatar>
      </IconButton>

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 250,
            overflow: 'visible',
            borderRadius: 2,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600} noWrap>
            {authState.user.displayName || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 1 }}>
            {authState.user.email}
          </Typography>
          <Chip
            label={roleInfo.label}
            color={roleInfo.color}
            size="small"
            sx={{ fontWeight: 500, fontSize: '0.75rem' }}
          />
        </Box>

        <Divider />

        {/* Menu Items */}
        <MenuItem onClick={() => handleNavigation(`/user/${authState.user?.uid}`)}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>My Public Profile</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleNavigation('/people/me')}>
          <ListItemIcon>
            <ProfileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>My Family Profile</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleNavigation('/settings')}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => handleNavigation('/feedback/submit')}>
          <ListItemIcon>
            <FeedbackIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Submit Feedback</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleNavigation('/feedback/my-requests')}>
          <ListItemIcon>
            <RequestsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>My Requests</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Sign Out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
